import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Calendar,
  BarChart3,
  AlertCircle,
  Lightbulb,
  DollarSign
} from 'lucide-react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useAuth } from '../../AuthContext';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { calculateMonthlySummary, calculateCategoryTotals } from '../../utils/budgeting/budgetCalculations';
import { getCostType, normalizeToMonthly } from '../../utils/budgeting/frequencyCalculations';
import { formatCurrency } from '../../countryConfig';

const Trends = ({ country, currency, onNavigate }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('6'); // 3, 6, 12 months
  const [trendData, setTrendData] = useState([]);
  const [categoryTrends, setCategoryTrends] = useState([]);
  const [budgetItems, setBudgetItems] = useState([]);
  const [fixedItems, setFixedItems] = useState([]);
  const [variableItems, setVariableItems] = useState([]);

  useEffect(() => {
    if (currentUser) {
      loadTrendData();
    }
  }, [currentUser, timeRange]);

  const loadTrendData = async () => {
    try {
      setLoading(true);

      const months = parseInt(timeRange);
      const endDate = new Date();
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - months);

      // Load budget items
      const budgetItemsRef = collection(db, `users/${currentUser.uid}/budgetItems`);
      const budgetItemsSnapshot = await getDocs(budgetItemsRef);

      const items = [];
      budgetItemsSnapshot.forEach(doc => {
        items.push({ id: doc.id, ...doc.data() });
      });

      setBudgetItems(items);

      // Categorize expenses into fixed and variable
      const expenses = items.filter(item => item.type === 'expense');
      const fixed = expenses.filter(item => getCostType(item.categoryName || item.name) === 'fixed');
      const variable = expenses.filter(item => getCostType(item.categoryName || item.name) === 'variable');

      setFixedItems(fixed);
      setVariableItems(variable);

      // Generate monthly data for the time range
      const monthlyData = [];
      const currentDate = new Date(startDate);

      while (currentDate <= endDate) {
        const month = currentDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

        // Calculate summary for this month (using current budget items as baseline)
        const summary = calculateMonthlySummary(items);
        const categoryTotals = calculateCategoryTotals(items);

        monthlyData.push({
          month,
          income: summary.totalMonthlyIncome,
          expenses: summary.totalMonthlyExpenses,
          surplus: summary.surplus,
          savingsRate: summary.savingsRate,
          fixed: summary.fixedCostsMonthly,
          variable: summary.variableCostsMonthly
        });

        currentDate.setMonth(currentDate.getMonth() + 1);
      }

      setTrendData(monthlyData);

      // Get top categories for trends
      const summary = calculateMonthlySummary(items);
      const categoryTotals = calculateCategoryTotals(items);
      setCategoryTrends(categoryTotals.slice(0, 5));

    } catch (error) {
      console.error('Error loading trend data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Budget Trends</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Historical analysis of your budget performance
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-gray-500" />
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          >
            <option value="3">Last 3 months</option>
            <option value="6">Last 6 months</option>
            <option value="12">Last 12 months</option>
          </select>
        </div>
      </div>

      {/* Income vs Expenses Trend */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Income vs Expenses Over Time
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={trendData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip
              formatter={(value) => formatCurrency(value, country)}
              contentStyle={{
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                border: 'none',
                borderRadius: '8px',
                color: 'white'
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="income"
              stroke="#10b981"
              strokeWidth={2}
              name="Income"
            />
            <Line
              type="monotone"
              dataKey="expenses"
              stroke="#ef4444"
              strokeWidth={2}
              name="Expenses"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Surplus Trend */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Monthly Surplus Trend
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={trendData}>
            <defs>
              <linearGradient id="colorSurplus" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip
              formatter={(value) => formatCurrency(value, country)}
              contentStyle={{
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                border: 'none',
                borderRadius: '8px',
                color: 'white'
              }}
            />
            <Area
              type="monotone"
              dataKey="surplus"
              stroke="#3b82f6"
              fillOpacity={1}
              fill="url(#colorSurplus)"
              name="Surplus"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Fixed vs Variable Costs */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Fixed vs Variable Costs
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={trendData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip
              formatter={(value) => formatCurrency(value, country)}
              contentStyle={{
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                border: 'none',
                borderRadius: '8px',
                color: 'white'
              }}
            />
            <Legend />
            <Bar dataKey="fixed" stackId="a" fill="#8b5cf6" name="Fixed Costs" />
            <Bar dataKey="variable" stackId="a" fill="#ec4899" name="Variable Costs" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Fixed vs Variable Breakdown - Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Fixed Costs List */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Fixed Costs
            </h3>
            <div className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 rounded-lg text-sm font-medium">
              {formatCurrency(fixedItems.reduce((sum, item) => sum + normalizeToMonthly(item.amount, item.frequency), 0), country)}/mo
            </div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Regular bills and contractual obligations that are hard to change
          </p>
          {fixedItems.length > 0 ? (
            <div className="space-y-2">
              {fixedItems.map((item, idx) => (
                <div
                  key={item.id || idx}
                  className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-900/10 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {item.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {item.categoryName}
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(normalizeToMonthly(item.amount, item.frequency), country)}/mo
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
              No fixed costs found
            </p>
          )}
        </div>

        {/* Variable/Discretionary Costs List */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Variable/Discretionary Costs
            </h3>
            <div className="px-3 py-1 bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-200 rounded-lg text-sm font-medium">
              {formatCurrency(variableItems.reduce((sum, item) => sum + normalizeToMonthly(item.amount, item.frequency), 0), country)}/mo
            </div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Flexible spending that can be adjusted or reduced
          </p>
          {variableItems.length > 0 ? (
            <div className="space-y-2">
              {variableItems.map((item, idx) => (
                <div
                  key={item.id || idx}
                  className="flex items-center justify-between p-3 bg-pink-50 dark:bg-pink-900/10 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {item.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {item.categoryName}
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(normalizeToMonthly(item.amount, item.frequency), country)}/mo
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
              No variable costs found
            </p>
          )}
        </div>
      </div>

      {/* Cost-Cutting Advice */}
      {variableItems.length > 0 && trendData.length > 0 && trendData[trendData.length - 1].surplus < 500 && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl p-6 border border-amber-200 dark:border-amber-800">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Lightbulb className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-amber-900 dark:text-amber-100 mb-2">
                Cost-Cutting Opportunities
              </h3>
              <p className="text-sm text-amber-800 dark:text-amber-200 mb-4">
                Your budget is tight with a surplus of {formatCurrency(trendData[trendData.length - 1].surplus, country)}/mo.
                Focus on reducing discretionary/variable spending to improve your financial health.
              </p>
              <div className="space-y-3">
                <div className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-2 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Top Areas to Reduce:
                  </h4>
                  <ul className="space-y-2 text-sm text-amber-800 dark:text-amber-200">
                    {variableItems
                      .sort((a, b) => normalizeToMonthly(b.amount, b.frequency) - normalizeToMonthly(a.amount, a.frequency))
                      .slice(0, 5)
                      .map((item, idx) => {
                        const monthly = normalizeToMonthly(item.amount, item.frequency);
                        const reduction10 = monthly * 0.1;
                        const reduction25 = monthly * 0.25;
                        return (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-amber-600 dark:text-amber-400 font-bold">•</span>
                            <div className="flex-1">
                              <span className="font-medium">{item.name}</span>
                              <span className="text-xs text-amber-700 dark:text-amber-300 ml-2">
                                ({formatCurrency(monthly, country)}/mo)
                              </span>
                              <div className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                                Save {formatCurrency(reduction10, country)}/mo with 10% cut, or {formatCurrency(reduction25, country)}/mo with 25% cut
                              </div>
                            </div>
                          </li>
                        );
                      })}
                  </ul>
                </div>
                <div className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-2">
                    💡 Quick Tips:
                  </h4>
                  <ul className="space-y-1 text-xs text-amber-800 dark:text-amber-200">
                    <li>• Review subscriptions - cancel what you don't use</li>
                    <li>• Reduce eating out frequency - cook at home more often</li>
                    <li>• Shop with a list to avoid impulse purchases</li>
                    <li>• Consider cheaper alternatives for regular purchases</li>
                    <li>• Set spending limits for discretionary categories</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Savings Rate Trend */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Savings Rate Over Time
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={trendData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              domain={[0, 100]}
              label={{ value: '%', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip
              formatter={(value) => `${value.toFixed(1)}%`}
              contentStyle={{
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                border: 'none',
                borderRadius: '8px',
                color: 'white'
              }}
            />
            <Line
              type="monotone"
              dataKey="savingsRate"
              stroke="#10b981"
              strokeWidth={3}
              name="Savings Rate"
              dot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Top Categories */}
      {categoryTrends.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Top 5 Expense Categories
          </h3>
          <div className="space-y-3">
            {categoryTrends.map((cat, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {cat.categoryName}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {cat.percentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${cat.percentage}%` }}
                    ></div>
                  </div>
                </div>
                <span className="ml-4 text-sm font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(cat.amount, country)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {trendData.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Avg Monthly Income</p>
            <p className="text-2xl font-bold text-green-600">
              {formatCurrency(
                trendData.reduce((sum, d) => sum + d.income, 0) / trendData.length,
                country
              )}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Avg Monthly Expenses</p>
            <p className="text-2xl font-bold text-red-600">
              {formatCurrency(
                trendData.reduce((sum, d) => sum + d.expenses, 0) / trendData.length,
                country
              )}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Avg Monthly Surplus</p>
            <p className="text-2xl font-bold text-blue-600">
              {formatCurrency(
                trendData.reduce((sum, d) => sum + d.surplus, 0) / trendData.length,
                country
              )}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Avg Savings Rate</p>
            <p className="text-2xl font-bold text-purple-600">
              {(trendData.reduce((sum, d) => sum + d.savingsRate, 0) / trendData.length).toFixed(1)}%
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Trends;
